import React, { useState, useEffect } from 'react';
import { CustomButton } from '../../types';
import { useTranslation } from 'react-i18next';
import { ButtonListProps } from '../../types/components';
import { Link } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import InsufficientTokensModal from '../modals/InsufficientTokensModal';
import { useClickBatcher } from '../../hooks/useClickBatcher';

/**
 * A reusable component for displaying a list of clickable buttons
 * Features:
 * - Displays counter buttons with their counts
 * - Handles paid button clicks with token balance checks
 * - Shows a modal when token balance is insufficient
 * - Supports different button states (pending, failed, disabled)
 */
function ButtonList({ 
  buttons, 
  onButtonClick, 
  disabled = false,
  onEdit,
  hasPendingClicks = () => false,
  getPendingClickCount = () => 0,
  hasFailedUpdates = () => false,
  isOwnerUrl = false,
  openCreateModal
}: ButtonListProps) {
  const { t } = useTranslation();
  const { isLoggedIn, user } = useUser();
  const [showInsufficientTokensModal, setShowInsufficientTokensModal] = useState(false);
  const [currentButtonCost, setCurrentButtonCost] = useState(0);
  
  // Get the event name from useClickBatcher
  const { INSUFFICIENT_TOKENS_EVENT } = useClickBatcher('', false);
  
  // Track pending token usage from batched clicks
  const [pendingTokenUsage, setPendingTokenUsage] = useState<Record<string, number>>({});
  
  // Calculate total pending token usage
  const totalPendingTokens = Object.entries(pendingTokenUsage).reduce((total, [buttonId, count]) => {
    const button = buttons.find(b => b._id === buttonId);
    const cost = button?.costToClick || 0;
    return total + (cost * count);
  }, 0);
  
  // Effective balance accounting for pending clicks
  const effectiveBalance = user.tokenBalance - totalPendingTokens;
  
  /**
   * Pre-calculate buttons that require login (ones with costToClick > 0)
   */
  const costButtonIds = React.useMemo(() => {
    return buttons
      .filter(button => button.costToClick && button.costToClick > 0)
      .map(button => button._id);
  }, [buttons]);
  
  // Check if there are any paid buttons
  const hasPaidButtons = React.useMemo(() => {
    return buttons.some(button => button.costToClick && button.costToClick > 0);
  }, [buttons]);
  
  /**
   * Listen for insufficient tokens events from the batcher
   */
  useEffect(() => {
    const handleInsufficientTokens = (event: Event) => {
      const customEvent = event as CustomEvent<{buttonId: string, cost: number}>;
      if (customEvent.detail) {
        const { cost } = customEvent.detail;
        setCurrentButtonCost(cost);
        setShowInsufficientTokensModal(true);
      }
    };
    
    window.addEventListener(INSUFFICIENT_TOKENS_EVENT, handleInsufficientTokens);
    
    return () => {
      window.removeEventListener(INSUFFICIENT_TOKENS_EVENT, handleInsufficientTokens);
    };
  }, [INSUFFICIENT_TOKENS_EVENT]);
  
  /**
   * Update pending token usage when buttons or pending clicks change
   */
  useEffect(() => {
    const newPendingTokenUsage: Record<string, number> = {};
    
    buttons.forEach(button => {
      if (button.costToClick && button.costToClick > 0 && hasPendingClicks(button._id)) {
        newPendingTokenUsage[button._id] = getPendingClickCount(button._id);
      }
    });
    
    setPendingTokenUsage(newPendingTokenUsage);
  }, [buttons, hasPendingClicks, getPendingClickCount]);
  
  /**
   * Handle button click event with token balance checks
   */
  const handleButtonClick = (id: string) => {
    // Skip if disabled
    if (disabled) return;
    
    // Find the button to check cost
    const button = buttons.find(b => b._id === id);
    
    // Skip if button has cost > 0 and this is an owner URL (counter owner)
    if (button && button.costToClick && button.costToClick > 0 && isOwnerUrl) {
      return; // Owners can't click cost buttons
    }
    
    // Check if button requires login (has cost > 0) and user is not logged in
    if (!isLoggedIn && costButtonIds.includes(id)) {
      // Show insufficient tokens modal instead of login modal
      const buttonCost = button?.costToClick || 0;
      setCurrentButtonCost(buttonCost);
      setShowInsufficientTokensModal(true);
      return;
    }
    
    // Get the button cost
    const buttonCost = button?.costToClick || 0;
    
    // Check if user has enough tokens for the button (accounting for pending usage)
    if (buttonCost > 0 && effectiveBalance < buttonCost) {
      // Show insufficient tokens modal
      setCurrentButtonCost(buttonCost);
      setShowInsufficientTokensModal(true);
      return;
    }
    
    // Track pending token usage for this button
    if (buttonCost > 0) {
      setPendingTokenUsage(prev => ({
        ...prev,
        [id]: (prev[id] || 0) + 1
      }));
    }
    
    // Call the parent click handler with the button cost
    onButtonClick(id);
  };

  /**
   * Handle login modal open
   */
  const handleOpenLoginModal = () => {
    // Use a custom event to communicate with the Navbar component
    const loginEvent = new CustomEvent('openLoginModal');
    window.dispatchEvent(loginEvent);
  };
  
  /**
   * Close the insufficient tokens modal
   */
  const handleCloseInsufficientTokensModal = () => {
    setShowInsufficientTokensModal(false);
  };

  return (
    <div className="button-list">
      {/* Owner warning message for paid buttons */}
      {isOwnerUrl && hasPaidButtons && (
        <div className="alert alert-warning mb-4" role="alert">
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          {t('buttons.ownerCannotClick', 'Counter owners cannot click cost buttons that have a cost to tap')}
        </div>
      )}
      
      {!buttons || buttons.length === 0 ? (
        <div className="text-center py-5">
          {isOwnerUrl ? (
            <>
              <i className="bi bi-plus-circle display-1 text-secondary opacity-50 mb-3" data-testid="empty-icon"></i>
              <p className="text-muted">{t('counter.noButtons', 'No buttons yet! Tap the \'Add New Button\' above to create custom buttons for tracking your taps.')}</p>
              {onEdit && (
                <button 
                  className="btn btn-primary mt-2"
                  onClick={onEdit}
                >
                  <i className="bi bi-plus-lg me-2"></i>
                  {t('buttons.addNewButton')}
                </button>
              )}
            </>
          ) : (
            <>
              <img 
                src="/images/favicon/favicon.svg" 
                alt="TapDonkey Logo" 
                className="mb-3" 
                width="70" 
                height="70"
                data-testid="empty-icon"
              />
              <p className="text-muted">{t('counter.noButtonsPublic', 'No buttons available. The counter owner hasn\'t added any buttons yet. If you\'re the owner and have lost access, you\'ll need to create a new counter.')}</p>
              <button 
                className="btn btn-primary mt-2"
                onClick={openCreateModal}
              >
                <i className="bi bi-plus-circle me-2"></i>
                {t('counters.createNew', 'Create New Counter')}
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="row g-4">
          {buttons.map(button => {
            const pendingCount = getPendingClickCount(button._id);
            const isPending = hasPendingClicks(button._id);
            const hasFailed = hasFailedUpdates(button._id);
            const buttonCost = button.costToClick || 0;
            
            // Determine if the button should be disabled
            // Only disable for global disabled state or owner URL with cost buttons
            const isButtonDisabled = disabled || (isOwnerUrl && buttonCost > 0);
            
            // Special title for owner buttons
            let buttonTitle = '';
            if (isOwnerUrl && buttonCost > 0) {
              buttonTitle = t('buttons.ownerCannotClick', 'Counter owners cannot click cost buttons');
            }
            
            return (
              <div key={button._id} className="col-md-6 col-lg-4">
                <button
                  id={`button-${button._id}`}
                  className={`btn btn-lg w-100 dynamic-button ${isButtonDisabled ? 'opacity-75 disabled' : ''} ${hasFailed ? 'border-danger' : ''}`}
                  style={{ 
                    '--button-color-value': button.color || '#6c757d'
                  } as React.CSSProperties}
                  data-color
                  onClick={() => handleButtonClick(button._id)}
                  disabled={isButtonDisabled}
                  aria-disabled={isButtonDisabled}
                  title={buttonTitle}
                >
                  <div className="d-flex align-items-center justify-content-center">
                    {button.icon && (
                      <i className={`bi bi-${button.icon} me-2 button-icon`} />
                    )}
                    <span>{button.name || t('buttons.unnamed')}</span>
                    <div className="d-flex align-items-center ms-2">
                      <div className="badge bg-light text-dark">
                        {button.count.toLocaleString()}
                      </div>
                      <div className={`badge ${isPending ? 'bg-secondary' : 'invisible'} ms-1`} title={isPending ? t('buttons.pendingClicks', 'Pending clicks') : ''}>
                        <i className="bi bi-hourglass-split me-1 small"></i>
                        +{isPending ? pendingCount : 0}
                      </div>
                    </div>
                  </div>
                  {/* Display cost to click if not 0 */}
                  {buttonCost > 0 && (
                    <div className="mt-1 small text-light">
                      <i className="bi bi-coin me-1"></i>
                      {t('buttons.costToClick', 'Cost')}: {buttonCost}
                      {isOwnerUrl && (
                        <i className="bi bi-lock-fill ms-2" title={t('buttons.ownerCannotClick', 'Counter owners cannot click cost buttons')}></i>
                      )}
                    </div>
                  )}
                  {hasFailed && (
                    <div className="position-absolute top-0 end-0 p-1">
                      <i className="bi bi-exclamation-triangle-fill text-danger" 
                         title={t('buttons.syncError', 'Sync error')}></i>
                    </div>
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}
      
      {/* Insufficient Tokens Modal */}
      <InsufficientTokensModal
        show={showInsufficientTokensModal}
        onHide={handleCloseInsufficientTokensModal}
        buttonCost={currentButtonCost}
      />
    </div>
  );
}

export default ButtonList; 